// realtime — the single seam for Supabase Realtime subscriptions.
//
// A Postgres-changes subscription is a LIVE-REFRESH NICETY, never a correctness
// dependency: every caller must ALSO load its data the normal way (query +
// refetch / invalidate on user action) so the UI stays correct if Realtime is
// unavailable, throttled, or later swapped out. Centralising creation here means
// the realtime backend is one seam — swap it, or no-op this file to disable live
// refresh entirely, without touching call sites. See docs/SUPABASE-EXIT-PLAN.md.

import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'

export interface TableWatch {
  table:   string
  event?:  '*' | 'INSERT' | 'UPDATE' | 'DELETE'   // default '*'
  schema?: string                                  // default 'public'
  filter?: string                                  // PostgREST filter, e.g. `id=eq.${x}`
}

type RealtimeHandle = ReturnType<typeof supabase.channel>

// Non-hook seam — for module-level singletons (e.g. useCurrentUser's shared
// `me:` channel) that manage their own lifecycle outside React. Creates +
// subscribes and returns the channel; tear down with removeRealtime(). React
// components should use the useRealtimeRefresh hook below instead.
export function subscribeToTables(
  channelName: string,
  watches: TableWatch[],
  onChange: () => void,
): RealtimeHandle {
  let ch = supabase.channel(channelName)
  for (const w of watches) {
    ch = ch.on(
      'postgres_changes',
      {
        event:  w.event  ?? '*',
        schema: w.schema ?? 'public',
        table:  w.table,
        ...(w.filter ? { filter: w.filter } : {}),
      },
      onChange,
    )
  }
  // Status callback (2026-06-11): subscribing bare made every failure mode
  // invisible — a channel that errored, timed out, or was closed server-side
  // just stopped delivering events and the board silently froze. Two jobs:
  //   1. postgres_changes has NO event replay — everything emitted while the
  //      socket was down (device sleep, network drop) is gone. Firing
  //      onChange() on every rejoin (SUBSCRIBED after the first) makes the
  //      subscriber refetch once and reconcile the missed window.
  //   2. Log error states once so dead-realtime sessions are visible in the
  //      console / Sentry breadcrumbs instead of indistinguishable from
  //      "no rows changed".
  let everSubscribed = false
  let lastLogged = ''
  ch.subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      if (everSubscribed) onChange()
      everSubscribed = true
      lastLogged = ''
    } else if (status !== lastLogged) {
      lastLogged = status
      console.warn(`realtime: channel "${channelName}" ${status}`)
    }
  })
  return ch
}

export function removeRealtime(ch: RealtimeHandle): void {
  supabase.removeChannel(ch)
}

// Subscribe to one or more table changes for the component's lifetime and run
// `onChange` whenever any fires. `channelName` MUST be unique per subscription
// instance and should encode the identity it depends on (e.g. `pallet-${id}`),
// because that — plus `enabled` — is what re-subscribes; `watches`/`onChange`
// are captured fresh each time the channel name changes (re-running on every
// render would thrash the socket). `enabled` lets callers gate on a ready id.
// `debounceMs` (trailing) collapses a burst of row changes into a single
// refetch — set it on the high-frequency unfiltered boards (Dashboard pulse,
// floor map) so a busy pack line doesn't trigger a refetch storm. 0 = fire
// immediately (right for low-frequency / filtered subscriptions).
export function useRealtimeRefresh(
  channelName: string,
  watches: TableWatch[],
  onChange: () => void,
  enabled = true,
  debounceMs = 0,
): void {
  useEffect(() => {
    if (!enabled) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const fire = debounceMs > 0
      ? () => { if (timer) clearTimeout(timer); timer = setTimeout(onChange, debounceMs) }
      : onChange
    const ch = subscribeToTables(channelName, watches, fire)
    return () => { if (timer) clearTimeout(timer); removeRealtime(ch) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelName, enabled, debounceMs])
}
